import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";

export interface TestForEvaluation {
  testId: number;
  testName: string;
  evaluatorType: number;
  assigned: number;
  pending: number;
  evaluated: number;
  reported: number;
}

export interface EvaluationQuestion {
  attemptId: number;
  questionNumber: number;
  maxMarks: number;
  obtainedMarks: number | null;
  remarks: string | null;
  studentResponse: string;
  attachments: Array<{ name: string; url: string }>;
  questionStatement: string;
  answerKey: string;
  questionCategory: number | null;
}

export interface EvaluationSection {
  sectionId: number;
  sectionName: string;
  questions: EvaluationQuestion[];
  showTotalMarksOnly: boolean;
  totalMarks: number | null;
  maxMarks: number | null;
}

export interface PaperForEvaluation {
  mappingId: number;
  testId: number;
  testName: string;
  testPaperUrl: string | null;
  evaluatorType: number;
  enrollmentId: number;
  employeeId: number;
  questionPaperUrl: string | null;
  questionPaperPassword: string | null;
  answerSheetUrl: string | null;
  sections: EvaluationSection[];
  hasMorePending: boolean;
  studentName: string | null;
  showStudentName: boolean;
  hasOnlineAnswers: boolean;
}

export interface AnnotationPoint {
  x: number;
  y: number;
  ss: string;
  lw: number;
  ex: number;
  ey: number;
  px: number;
  iterationId?: number;
  type?: "stroke" | "highlighter" | "text" | "arrow" | "circle" | "rectangle";
  text?: string;
  fontSize?: number;
  width?: number;
  height?: number;
  id?: string;
}

export interface PaperPosition {
  angle: number;
  scale: number;
}

class DvsEvaluationService {
  private baseUrl = `${Config.nodeSiteUrl}v1/dvs-evaluation`;

  async getTestsForEvaluation(): Promise<TestForEvaluation[]> {
    const response: any = await myAxios.get(`${this.baseUrl}/tests`);
    return response.data?.data || [];
  }

  async getNextPaper(
    testId: number,
    evaluatorType: number,
  ): Promise<PaperForEvaluation | null> {
    const response: any = await myAxios.get(
      `${this.baseUrl}/tests/${testId}/next-paper?evaluatorType=${evaluatorType}`,
    );
    return response.data?.data || null;
  }

  async saveAnnotation(
    mappingId: number,
    page: number,
    data: AnnotationPoint[],
  ): Promise<void> {
    await myAxios.post(`${this.baseUrl}/annotations`, { mappingId, page, data });
  }

  async getAnnotation(
    mappingId: number,
    page: number,
  ): Promise<AnnotationPoint[]> {
    const response: any = await myAxios.get(
      `${this.baseUrl}/annotations?mappingId=${mappingId}&page=${page}`,
    );
    return response.data?.data || [];
  }

  async undoAnnotation(
    mappingId: number,
    page: number,
  ): Promise<AnnotationPoint[]> {
    const response: any = await myAxios.post(`${this.baseUrl}/annotations/undo`, {
      mappingId,
      page,
    });
    return response.data?.data || [];
  }

  async clearAnnotation(mappingId: number, page: number): Promise<void> {
    await myAxios.post(`${this.baseUrl}/annotations/clear`, { mappingId, page });
  }

  async savePaperPosition(
    mappingId: number,
    page: number,
    angle: number,
    scale: number,
  ): Promise<void> {
    await myAxios.post(`${this.baseUrl}/paper-position`, {
      mappingId,
      page,
      angle,
      scale,
    });
  }

  async getPaperPosition(
    mappingId: number,
    page: number,
  ): Promise<PaperPosition> {
    const response: any = await myAxios.get(
      `${this.baseUrl}/paper-position?mappingId=${mappingId}&page=${page}`,
    );
    return response.data?.data || { angle: 0, scale: 1 };
  }

  async updateQuestionMarks(
    attemptId: number,
    type: "marks" | "remarks",
    value: string,
  ): Promise<{ success: boolean; error?: string }> {
    const response: any = await myAxios.patch(`${this.baseUrl}/question-marks`, {
      attemptId,
      type,
      value,
    });
    return response.data;
  }

  async submitEvaluation(params: {
    mappingId: number;
    evaluatorType: number;
    marks?: Array<{ attemptId: number; obtainedMarks: number }>;
  }): Promise<{ success: boolean; hasMorePending: boolean; error?: string; errors?: Record<string, string> }> {
    const response: any = await myAxios.post(`${this.baseUrl}/submit`, params);
    return response.data;
  }

  async skipPaper(
    mappingId: number,
    evaluatorType: number,
  ): Promise<void> {
    await myAxios.post(`${this.baseUrl}/skip`, { mappingId, evaluatorType });
  }

  async reportIssue(
    mappingId: number,
    comment: string,
  ): Promise<{ success: boolean; hasMorePending: boolean }> {
    const response: any = await myAxios.post(`${this.baseUrl}/report-issue`, {
      mappingId,
      comment,
    });
    return response.data;
  }
}

export default new DvsEvaluationService();
